import {
  OptionalStateCarts,
  StateCart,
  StateCartDiscountCode
} from '@spa/lib/store/carts/CartsState';
import { actionChannel, put, select, take } from 'typed-redux-saga';
import {
  addDiscountCodeSucceededType,
  addDiscountCodeToCurrentCartsFailedType,
  addDiscountCodeToCurrentCartsRequested,
  addDiscountCodeToCurrentCartsSucceededType,
  addEmployeeDiscountCodeToCurrentCartsFailedType,
  addEmployeeDiscountCodeToCurrentCartsRequested,
  addEmployeeDiscountCodeToCurrentCartsSucceededType,
  addProductsToCurrentCartSucceededType,
  addToCurrentCartSucceededType,
  currentCartsChangedOutsideOfApp,
  currentCartsChangedOutsideOfAppType,
  deleteCurrentCartEntrySucceededType,
  removeAllDiscountCodesFromCartsFailedType,
  removeAllDiscountCodesFromCartsRequested,
  removeAllDiscountCodesFromCartsSucceededType,
  removeDiscountCodeFromCartsFailedType,
  removeDiscountCodeFromCartsRequested,
  removeDiscountCodeFromCartsSucceededType,
  removeDiscountCodeSucceededType,
  setCartType,
  updateCurrentCartEntryQuantitySucceededType
} from '@spa/lib/store/carts/cartsSlice';
import { RootState } from '@spa/lib/store/RootState';
import generateActionId from '@spa/lib/generateActionId';
import { hydrateType } from '@spa/lib/store/HydrateAction';
import selectCarts from '@spa/lib/store/carts/selectors/selectCarts';
import selectGeneralSettings from '@spa/lib/store/settings/selectors/selectGeneralSettings';
import { selectIsInEmployeeCustomerGroup } from '@spa/lib/store/user/selectors/selectIsInEmployeeCustomerGroup';

// Actions that can change what the employee-discount reconciliation should
// decide: actual cart-content mutations (entries or discount codes, whichever
// action ends up applying them - some flows update carts via `setCart`,
// others via the singular per-cart add/removeDiscountCodeSucceeded reducers),
// cross-tab updates, hydration (also how retrospective employee-group
// linking reaches the client, see findAndPrepareCmsPageServerProps), and the
// explicit user-intent actions ("apply employee discount" / "use another
// code" / "redeem a voucher"). This mirrors the trigger-list pattern used by
// reconcileGiftCardOnCartEntryChangeSaga instead of subscribing to every
// action in the store.
//
// Every aggregate discount-mutation completion is included because the
// pending guard below suppresses intermediate cart updates for the complete
// sequential HD/C&C operation. Re-evaluating when that pending bucket clears
// prevents a blocker or conflicting discount that arrived mid-operation from
// being left unreconciled. Failures also wake the saga, but an unchanged
// failed mutation is not retried because of the mutation-key deduplication.
export const reconcileEmployeeDiscountTriggerTypes = [
  setCartType,
  addToCurrentCartSucceededType,
  addProductsToCurrentCartSucceededType,
  updateCurrentCartEntryQuantitySucceededType,
  deleteCurrentCartEntrySucceededType,
  addDiscountCodeSucceededType,
  removeDiscountCodeSucceededType,
  addDiscountCodeToCurrentCartsSucceededType,
  addDiscountCodeToCurrentCartsFailedType,
  addEmployeeDiscountCodeToCurrentCartsSucceededType,
  addEmployeeDiscountCodeToCurrentCartsFailedType,
  removeDiscountCodeFromCartsSucceededType,
  removeDiscountCodeFromCartsFailedType,
  removeAllDiscountCodesFromCartsSucceededType,
  removeAllDiscountCodesFromCartsFailedType,
  currentCartsChangedOutsideOfAppType,
  hydrateType,
  addEmployeeDiscountCodeToCurrentCartsRequested.type,
  addDiscountCodeToCurrentCartsRequested.type,
  removeAllDiscountCodesFromCartsRequested.type
];

type EmployeeDiscountIntent = 'automatic' | 'employee' | 'other';

export interface EmployeeDiscountCartSnapshot {
  readonly activeEmployeeCodeIds: ReadonlyArray<string>;
  readonly allCartsWithEntriesHaveActiveEmployeeDiscount: boolean;
  readonly hasActiveEmployeeDiscount: boolean;
  readonly hasEntries: boolean;
  readonly hasProductDiscountBlocker: boolean;
  readonly hasValidNonEmployeeDiscount: boolean;
  readonly signature: string;
}

export type EmployeeDiscountReconciliationDecision =
  | { readonly type: 'apply' }
  | { readonly type: 'none' }
  | { readonly type: 'remove'; readonly codeId: string };

const isActiveEmployeeDiscount = ({ isEmployeeDiscount, valid }: StateCartDiscountCode): boolean =>
  isEmployeeDiscount && valid;

export const createEmployeeDiscountCartSnapshot = (
  carts: OptionalStateCarts
): EmployeeDiscountCartSnapshot => {
  const cartsWithEntries = Object.values(carts).filter(
    (cart): cart is StateCart => cart !== null && cart.entries.length > 0
  );
  const discountCodes = Object.values(carts).flatMap((cart) => cart?.discountCodes ?? []);
  const activeEmployeeCodeIds = Array.from(
    new Set(discountCodes.filter(isActiveEmployeeDiscount).map(({ id }) => id))
  );
  const hasProductDiscountBlocker = Object.values(carts).some((cart) =>
    cart?.entries.some(({ hasProductDiscount }) => hasProductDiscount === true)
  );
  const hasValidNonEmployeeDiscount = discountCodes.some(
    ({ isEmployeeDiscount, valid }) => !isEmployeeDiscount && valid
  );
  const allCartsWithEntriesHaveActiveEmployeeDiscount = cartsWithEntries.every((cart) =>
    (cart.discountCodes ?? []).some(isActiveEmployeeDiscount)
  );
  const signature = JSON.stringify(
    Object.entries(carts).map(([customOrderType, cart]) => [
      customOrderType,
      cart?.code ?? null,
      (cart?.entries.length ?? 0) > 0,
      cart?.entries
        .filter(({ hasProductDiscount }) => hasProductDiscount === true)
        .map(({ id }) => id),
      (cart?.discountCodes ?? []).map(({ id, isEmployeeDiscount, valid }) => [
        id,
        isEmployeeDiscount,
        valid
      ])
    ])
  );

  return {
    activeEmployeeCodeIds,
    allCartsWithEntriesHaveActiveEmployeeDiscount,
    hasActiveEmployeeDiscount: activeEmployeeCodeIds.length > 0,
    hasEntries: cartsWithEntries.length > 0,
    hasProductDiscountBlocker,
    hasValidNonEmployeeDiscount,
    signature
  };
};

export const getEmployeeDiscountReconciliationDecision = (
  snapshot: EmployeeDiscountCartSnapshot,
  shouldApplyEmployeeDiscount: boolean
): EmployeeDiscountReconciliationDecision => {
  if (!snapshot.hasEntries) {
    return { type: 'none' };
  }

  if (snapshot.hasProductDiscountBlocker) {
    const [codeId] = snapshot.activeEmployeeCodeIds;
    return codeId === undefined ? { type: 'none' } : { type: 'remove', codeId };
  }

  if (
    shouldApplyEmployeeDiscount &&
    !snapshot.hasValidNonEmployeeDiscount &&
    !snapshot.allCartsWithEntriesHaveActiveEmployeeDiscount
  ) {
    return { type: 'apply' };
  }

  return { type: 'none' };
};

const selectCartsLoaded = ({ carts: { cartsLoaded } }: RootState): boolean => cartsLoaded;
const selectDiscountMutationPending = ({ carts: { pageActions } }: RootState): boolean =>
  pageActions.addDiscountCodeToCurrentCarts.pending.length > 0 ||
  pageActions.addEmployeeDiscountCodeToCurrentCarts.pending.length > 0 ||
  pageActions.removeAllDiscountCodesFromCarts.pending.length > 0 ||
  pageActions.removeDiscountCodeFromCarts.pending.length > 0;

function* reconcileEmployeeDiscountSaga(): Generator {
  const actionChannelForReconciliation = yield* actionChannel(
    reconcileEmployeeDiscountTriggerTypes
  );
  let intent: EmployeeDiscountIntent = 'automatic';
  let isInitialReconciliation = true;
  let lastSnapshot: EmployeeDiscountCartSnapshot | null = null;
  let lastRequestedMutationKey: string | null = null;

  while (true) {
    const isInitialEvaluation = isInitialReconciliation;
    const action = isInitialEvaluation ? null : yield* take(actionChannelForReconciliation);
    isInitialReconciliation = false;
    let intentSetByAction = false;

    if (action !== null && addEmployeeDiscountCodeToCurrentCartsRequested.match(action)) {
      intent = 'employee';
      intentSetByAction = true;
    } else if (
      action !== null &&
      (addDiscountCodeToCurrentCartsRequested.match(action) ||
        removeAllDiscountCodesFromCartsRequested.match(action))
    ) {
      intent = 'other';
      intentSetByAction = true;
    }

    const cartsLoaded: boolean = yield* select(selectCartsLoaded);
    if (!cartsLoaded) {
      continue;
    }

    const carts: OptionalStateCarts = yield* select(selectCarts);
    const snapshot = createEmployeeDiscountCartSnapshot(carts);
    const cartStateChanged = lastSnapshot?.signature !== snapshot.signature;

    if (cartStateChanged) {
      if (!intentSetByAction) {
        if (snapshot.hasValidNonEmployeeDiscount) {
          intent = 'other';
        } else if (
          snapshot.hasActiveEmployeeDiscount &&
          (intent !== 'other' || (action !== null && currentCartsChangedOutsideOfApp.match(action)))
        ) {
          intent = 'employee';
        } else if (
          lastSnapshot?.hasActiveEmployeeDiscount === true &&
          action !== null &&
          currentCartsChangedOutsideOfApp.match(action) &&
          !snapshot.hasProductDiscountBlocker &&
          snapshot.hasEntries
        ) {
          intent = 'other';
        }
      }
      lastSnapshot = snapshot;
    }

    const { employeeDiscountEnabled } = yield* select(selectGeneralSettings);
    const isEmployee = yield* select(selectIsInEmployeeCustomerGroup);
    if (!employeeDiscountEnabled || !isEmployee) {
      continue;
    }

    const discountMutationPending: boolean = yield* select(selectDiscountMutationPending);
    if (discountMutationPending) {
      continue;
    }

    // A direct load has no persisted evidence that an eligible employee chose
    // employee mode. It may still remove an active employee code that has
    // become invalid, but applying one remains driven by a subsequent cart
    // transition or an explicit user action.
    const decision = getEmployeeDiscountReconciliationDecision(
      snapshot,
      !isInitialEvaluation && intent !== 'other'
    );
    const mutationKey =
      decision.type === 'remove'
        ? `remove:${decision.codeId}:${snapshot.signature}`
        : `${decision.type}:${snapshot.signature}`;

    if (decision.type === 'none' || mutationKey === lastRequestedMutationKey) {
      continue;
    }

    lastRequestedMutationKey = mutationKey;
    const requestedActionId = generateActionId();
    if (decision.type === 'remove') {
      yield* put(
        removeDiscountCodeFromCartsRequested({
          codeId: decision.codeId,
          requestedActionId
        })
      );
    } else {
      yield* put(addEmployeeDiscountCodeToCurrentCartsRequested({ requestedActionId }));
    }
  }
}

export default reconcileEmployeeDiscountSaga;
